/**
 * CategoryView — reads `:category` from the route, validates it against
 * `CATEGORIES`, resolves the current user's role, and renders the
 * role-specific view for that category.
 *
 * The annotator role's list view (`AnnotatorCategory`, Task 5) is wired in
 * below for the `/labeling-overview/:category` route (no `:projectId`), and
 * the annotator role's study view (`StudyView`, Task 6) for
 * `/labeling-overview/:category/:projectId`. The admin role's control room
 * (`AdminControlRoom`, Task 7) is wired in for `/labeling-overview/:category`
 * with no `:projectId`, and its project-detail route (`AdminProjectDetail`,
 * Task 8) for `/labeling-overview/:category/:projectId`. The internal role's
 * read-only category view (`InternalCategory`, Task 10) stops at the
 * worklist level — there's no internal-role project-detail route, so it's
 * wired for `/labeling-overview/:category` regardless of a stray `:projectId`.
 *
 * All three `LabelingRole` values are handled below (annotator/admin split
 * on `:projectId`, internal ignores it), so there's no remaining role×route
 * combination to fall back on — Task 11 removed the generic "coming in a
 * later task" placeholder that used to trail this chain, since it had gone
 * unreachable.
 *
 * **Factory vs non-factory categories (Task 12).** Only "3d-segmentation"
 * is factory-managed (accept/request-more/enrollments/phases) — that's the
 * role branch above, untouched. Reporter Studies / Triage Validation /
 * Other are the existing labeling projects with real staging data and no
 * factory admin/internal surface, so every role sees the same flat
 * `NonFactoryCategory` project list for those; `:projectId` is unused there
 * because a row click navigates OUT to `/labeling/reporting/:id` or
 * `/labeling?project=`, never to a nested route under
 * `/labeling-overview/:category/...`.
 */

import { Navigate, useParams } from "react-router-dom";

import { useLabelingRole } from "../../../hooks/useLabelingRole";
import { CATEGORIES, type LabelingCategoryKey } from "../../../utils/labelingCategories";
import { AdminControlRoom } from "./admin/AdminControlRoom";
import { AdminProjectDetail } from "./admin/AdminProjectDetail";
import { AnnotatorCategory } from "./AnnotatorCategory";
import { InternalCategory } from "./InternalCategory";
import { NonFactoryCategory } from "./NonFactoryCategory";
import { StudyView } from "./StudyView";

const FACTORY_CATEGORIES = new Set<LabelingCategoryKey>(["3d-segmentation"]);

function isCategoryKey(value: string | undefined): value is LabelingCategoryKey {
  return CATEGORIES.some((c) => c.key === value);
}

export function CategoryView() {
  const { category, projectId } = useParams<{ category: string; projectId?: string }>();
  const role = useLabelingRole();

  if (!isCategoryKey(category)) {
    // Unknown/stale category slug — bounce back to the tile grid rather
    // than rendering a dead-end "not found" panel one level deep.
    return <Navigate to="/labeling-overview" replace />;
  }

  if (!FACTORY_CATEGORIES.has(category)) {
    // Reporter Studies / Triage Validation / Other have no factory surface —
    // every role sees the same flat project list, and `projectId` is unused
    // (clicks route OUT of this tree entirely).
    return <NonFactoryCategory categoryKey={category} />;
  }

  if (role === "internal") {
    return <InternalCategory categoryKey={category} />;
  }

  if (role === "admin" || role === "manager") {
    return projectId ? (
      <AdminProjectDetail categoryKey={category} projectId={projectId} />
    ) : (
      <AdminControlRoom categoryKey={category} />
    );
  }

  // role === "annotator"
  return projectId ? (
    <StudyView categoryKey={category} worklistProjectId={projectId} />
  ) : (
    <AnnotatorCategory categoryKey={category} />
  );
}

export default CategoryView;
